/**
 * Apply export-quote bulk color / glazing changes across all drawings.
 * Individual drawings can still be edited afterward to override.
 */

import { sumAdditionalItemsPrice } from './additionalItems';
import { calculateConfiguratorPrice } from './pdfPricing';
import {
  applyDrawingPriceAdjustment,
  applyVekaProfileDiscount,
} from './pricingAdjustments';
import { syncSvgFrameColorsToConfig } from './recolorQuoteDrawingSvg';
import { detectPageType } from './vmkQuoteTable';

const round2 = (value) => Math.round(Number(value) * 100) / 100;

export function applyBulkExteriorColor(config = {}, colorId) {
  if (!colorId) return config;
  return {
    ...config,
    color: colorId,
    exteriorColor: colorId,
  };
}

export function applyBulkInteriorColor(config = {}, colorId) {
  if (!colorId) return config;
  return {
    ...config,
    interiorFrameColor: colorId,
    interiorColor: colorId,
  };
}

export function applyBulkDraaideelColor(config = {}, colorId) {
  if (!colorId) return config;
  return {
    ...config,
    draaideelColor: colorId,
  };
}

export function applyBulkGlazing(config = {}, glazingId) {
  if (!glazingId) return config;
  const next = {
    ...config,
    glazingType: glazingId,
  };
  if (config.rightGlazingType) {
    next.rightGlazingType = glazingId;
  }
  return next;
}

/**
 * Unit price (ex VAT) matching Summary save: PDF configurator + extras + VEKA + drawing adj.
 * Does not include admin margin / workers / surcharge (those stay in the stored delta).
 */
export function computeDrawingUnitPriceExVat(config = {}, pageType) {
  const type = pageType || detectPageType(config);
  const { price, hasPricing } = calculateConfiguratorPrice(config, type);
  if (!hasPricing) {
    return { basePrice: 0, unitPrice: 0, hasPricing: false };
  }
  let unitPrice = price + sumAdditionalItemsPrice(config.additionalItems);
  unitPrice = applyVekaProfileDiscount(unitPrice, config.profileSystem);
  unitPrice = applyDrawingPriceAdjustment(unitPrice, config);
  return { basePrice: price, unitPrice: round2(unitPrice), hasPricing: true };
}

/** Reprice a drawing after config change; preserve admin margin baked into stored total. */
export function withRecalculatedDrawingPricing(drawing, nextConfig) {
  if (!drawing) return drawing;
  const prevConfig = drawing.config || {};
  const pageType = drawing.type || detectPageType(nextConfig || prevConfig);
  const prevUnit = computeDrawingUnitPriceExVat(prevConfig, pageType);
  const nextUnit = computeDrawingUnitPriceExVat(nextConfig || prevConfig, pageType);

  const prevPricing = drawing.pricing || {};
  let pricing = { ...prevPricing };

  if (nextUnit.hasPricing && prevUnit.hasPricing && prevUnit.unitPrice > 0) {
    // Scale stored totals so admin margin / workers baked into pricing.totalPrice
    // stay proportional when glazing (etc.) changes the PDF unit price.
    const scale = nextUnit.unitPrice / prevUnit.unitPrice;
    const prevTotal = Number(prevPricing.totalPrice) || 0;
    const prevFinal = Number(prevPricing.finalBasePrice);
    pricing = {
      ...prevPricing,
      basePrice: nextUnit.basePrice,
      totalPrice: round2(prevTotal * scale),
      finalBasePrice: round2(
        (Number.isFinite(prevFinal) ? prevFinal : prevTotal) * scale
      ),
    };
  } else if (nextUnit.hasPricing) {
    pricing = {
      ...prevPricing,
      basePrice: nextUnit.basePrice,
      totalPrice: nextUnit.unitPrice,
      finalBasePrice: nextUnit.unitPrice,
    };
  }

  const width = Number(nextConfig?.width) || 0;
  const height = Number(nextConfig?.height) || 0;
  if (width > 0 && height > 0) {
    pricing.areaSqm = ((width * height) / 1_000_000).toFixed(2);
  }

  return {
    ...drawing,
    config: nextConfig,
    pricing,
  };
}

export function mapDrawingConfig(drawing, configMapper) {
  if (!drawing || typeof configMapper !== 'function') return drawing;
  const config = drawing.config || {};
  const nextConfig = configMapper(config);
  const repriced = withRecalculatedDrawingPricing(drawing, nextConfig);
  // Always force SVG fills to the new config colors (detects current fills in the SVG)
  const svgData = syncSvgFrameColorsToConfig(drawing.svgData, nextConfig);
  return {
    ...repriced,
    svgData: svgData || repriced.svgData,
  };
}

/** Shared exterior id if every drawing matches; otherwise null (mixed). */
export function getCommonExteriorColorId(drawings = []) {
  const ids = drawings
    .map((d) => d?.config?.exteriorColor || d?.config?.color)
    .filter(Boolean);
  if (!ids.length) return null;
  const first = ids[0];
  return ids.every((id) => id === first) ? first : null;
}

export function getCommonInteriorColorId(drawings = []) {
  const ids = drawings
    .map((d) => d?.config?.interiorFrameColor || d?.config?.interiorColor)
    .filter(Boolean);
  if (!ids.length) return null;
  const first = ids[0];
  return ids.every((id) => id === first) ? first : null;
}

export function getCommonDraaideelColorId(drawings = []) {
  const ids = drawings
    .map((d) => d?.config?.draaideelColor || d?.config?.exteriorColor || d?.config?.color)
    .filter(Boolean);
  if (!ids.length) return null;
  const first = ids[0];
  return ids.every((id) => id === first) ? first : null;
}

export function getCommonGlazingId(drawings = []) {
  const ids = drawings.map((d) => d?.config?.glazingType).filter(Boolean);
  if (!ids.length) return null;
  const first = ids[0];
  return ids.every((id) => id === first) ? first : null;
}
